import { subYears } from 'date-fns';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { InputPhone } from '@/components/input-phone';
import { ExistingPhoneDisplay } from '@/components/input-phone/existing-phone-display';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { SmsConsentDisclaimer } from '@/components/sms-consent-disclaimer';
import mixpanelService from '@/core/clients/mixpanel';
import type { FinishProfileFormType } from '@/core/hooks/use-finish-profile-form';
import { useFinishProfileForm } from '@/core/hooks/use-finish-profile-form';
import { Button, ControlledInput, showErrorMessage, Text } from '@/ui';
import { Loader } from '@/ui/loader';

interface EntryFinishSignupProps {
  onDone: (newPhone?: string) => void;
  onSkip?: () => void;
  onBack?: () => void;
}

export function EntryFinishSignup({
  onDone,
  onSkip,
  onBack,
}: EntryFinishSignupProps) {
  const { styles } = useStyles(stylesheet);

  const {
    control,
    trigger,
    setValue,
    handleSubmit,
    isValid,
    handleDobChange,
    setPhone,
    setCountry,
    phoneKnown,
    parsedExistingPhone,
    isValidPhoneNumber,
    isSaving,
    submitProfile,
  } = useFinishProfileForm();

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Sign Up Finish',
    });
  }, []);

  const handleContinue = (data: FinishProfileFormType) => {
    mixpanelService.track('Button Clicked', {
      'Button Name': 'Sign Up Finish',
    });

    submitProfile(data, {
      // The opt-in checkbox is gone: submitting contact information is the
      // consent, per the disclaimer shown alongside these fields.
      smsOptIn: true,
      onDone,
      onError: showErrorMessage,
    });
  };

  const handleSkip = () => {
    onSkip?.();
  };

  if (isSaving) {
    return (
      <View style={styles.loaderContainer}>
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <View style={styles.card}>
      {onBack && (
        <NavigationButton
          type={NavigationButtonTypes.back_with_text}
          onPress={onBack}
        />
      )}

      <View style={styles.titleContainer}>
        <Text tx="entry.finishSignup.title" type="title1" />
        <Text
          tx={
            phoneKnown
              ? 'entry.finishSignup.subtitlePhone'
              : 'entry.finishSignup.subtitleEmail'
          }
          type="bodyRegular"
          style={styles.cardSubtitle}
        />
      </View>

      <View style={styles.inputsContainer}>
        <View style={styles.nameRow}>
          <ControlledInput
            control={control}
            name="firstName"
            label="First name"
            containerStyle={{ flex: 1 }}
            onBlur={() => trigger('firstName')}
            onChangeText={(text) => {
              setValue('firstName', text);
              trigger('firstName');
            }}
            onSubmitEditing={handleSubmit(handleContinue)}
          />
          <ControlledInput
            control={control}
            name="lastName"
            label="Last name"
            containerStyle={{ flex: 1 }}
            onBlur={() => trigger('lastName')}
            onChangeText={(text) => {
              setValue('lastName', text);
              trigger('lastName');
            }}
            onSubmitEditing={handleSubmit(handleContinue)}
          />
        </View>

        <ControlledInput
          control={control}
          name="email"
          label="Email"
          spellCheck={false}
          onBlur={() => trigger('email')}
          onChangeText={(text) => {
            setValue('email', text);
            trigger('email');
          }}
          autoComplete="email"
          onSubmitEditing={handleSubmit(handleContinue)}
        />

        <ControlledInput
          control={control}
          name="dob"
          label="Date of birth - MM/DD/YYYY"
          type="date"
          keyboardType="number-pad"
          inputMode="numeric"
          onBlur={() => trigger('dob')}
          onChangeText={handleDobChange}
          maximumDate={subYears(new Date(), 21)}
          defaultDate={subYears(new Date(), 25)}
          onSubmitEditing={handleSubmit(handleContinue)}
        />

        {phoneKnown ? (
          <ExistingPhoneDisplay phone={parsedExistingPhone} />
        ) : (
          <InputPhone
            onChangeText={setPhone}
            onCountryChange={setCountry}
            placeholder={t('entry.phonePlaceholder')}
            onSubmitEditing={handleSubmit(handleContinue)}
          />
        )}
      </View>

      <SmsConsentDisclaimer />

      <View style={styles.buttonContainer}>
        <Button
          tx="entry.finishSignup.continue"
          type="primary"
          onPress={handleSubmit(handleContinue)}
          disabled={!isValid || !isValidPhoneNumber}
        />

        {onSkip && (
          <Button
            tx="entry.finishSignup.setUpLater"
            type="ghost"
            onPress={handleSkip}
          />
        )}
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  card: {
    width: '100%',
    maxWidth: 600,
    paddingHorizontal: 18,
    gap: 20,
    flexGrow: {
      xs: 1,
      md: 0,
    },
  },
  titleContainer: {
    gap: 8,
    marginTop: 8,
  },
  cardSubtitle: {
    color: theme.colors.text.secondary,
  },
  inputsContainer: {
    gap: 12,
  },
  nameRow: {
    flexDirection: 'row',
    gap: 8,
  },
  loaderContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background_1,
  },
  buttonContainer: {
    gap: 12,
    alignItems: 'center',
    marginTop: {
      xs: 'auto',
      md: 0,
    },
  },
}));
